/**
 * Wave-1 A6/A7 (21-Sep-2026) — presentation-layer route guard.
 *
 * Every /app/* route resolves to the sidebar module key that owns it
 * (NAV_GROUPS is the single source of truth; PREFIX_RULES covers detail
 * pages and settings sub-pages with no sidebar entry). When the user's
 * effective modules don't include that key, an access-denied page renders
 * INSTEAD of the page — no flash of protected content.
 *
 * This does not replace the server-side perm() checks (A1–A5); both exist.
 */
import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { NAV_GROUPS } from './Sidebar';

// Self-service / open pages every authenticated user may render.
const PUBLIC_PATHS = [
  '/app',
  '/app/my-tasks',
  '/app/approvals',
  '/app/profile',
  '/app/hr/me',
  '/app/hr/apply-leave',
  '/app/hr/my-training',
  '/app/hr/my-assets',
  '/app/hr/my-travel',
  '/app/hr/travel/requests',   // §3 Enforce_Mode_Approval_Gates: self-service travel form
  '/app/hr/travel/requests/',
  '/app/hr/policies',
  '/app/hr/salary-advances',   // §8: salary advance self-service
];

// A7 — screens that must not render at all below super-admin.
const SUPER_ADMIN_ONLY = [
  { prefix: '/app/settings/data-export', label: 'Data Export & Backup' },
  { prefix: '/app/vendor-portal-admin', label: 'Vendor Portal Admin' },
];

// Prefix rules for /app/* routes with no sidebar entry (detail pages,
// settings sub-pages, MIS). Keys match the legacy allowed_modules values
// the sidebar uses. Longest prefix wins.
const PREFIX_RULES = [
  ['/app/settings', 'settings', 'Settings'],
  ['/app/user-management', 'settings', 'User Management'],
  ['/app/mis', 'reports', 'MIS & Analytics'],
  ['/app/reports', 'reports', 'Reports'],
  ['/app/crm', 'crm', 'Business Development'],
  ['/app/tendering', 'tendering', 'Tendering'],
  ['/app/estimation', 'tendering', 'Tendering & Estimation'],
  ['/app/projects', 'projects', 'Projects'],
  ['/app/project-planning', 'projects', 'Project Planning'],
  ['/app/risks', 'projects', 'Risk Register'],
  ['/app/cost-control', 'projects', 'Cost Control'],
  ['/app/closeout', 'projects', 'Closeout & Handover'],
  ['/app/engineering', 'engineering', 'Engineering & Design'],
  ['/app/safety', 'hse', 'Safety / HSE'],
  ['/app/hse', 'hse', 'HSE'],
  // Wave-1 C2 correction — Quality screens belong to the `quality` module
  // key, not `hse`; the sidebar keys were the drift, the guard exposed it.
  ['/app/quality', 'quality', 'Quality'],
  ['/app/dpr', 'dpr', 'DPR Module'],
  ['/app/procurement', 'procurement', 'Procurement'],
  ['/app/mrp', 'procurement', 'Material Planning'],
  ['/app/material-gaps', 'procurement', 'Material Gaps'],
  ['/app/inventory', 'inventory', 'Inventory'],
  ['/app/stock-register', 'inventory', 'Stock Register'],
  ['/app/stores', 'inventory', 'Stores'],
  ['/app/petty-cash', 'petty_cash', 'Petty Cash'],
  ['/app/overview/fraud-alerts', 'fraud_alerts', 'Fraud Alerts'],
  ['/app/fuel', 'fuel', 'Fuel Management'],
  ['/app/equipment', 'equipment', 'Plant & Machinery'],
  ['/app/subcontractors', 'subcontractors', 'Subcontractors'],
  ['/app/labour-master', 'labour_master', 'Labour Master'],
  ['/app/labour-dashboard', 'labour_attendance', 'Labour Dashboard'],
  ['/app/labour', 'labour_attendance', 'Labour Attendance'],
  ['/app/kiosk-security', 'labour_attendance', 'Kiosk Security'],
  ['/app/hr', 'hr', 'People & HR'],
  ['/app/it', 'it_assets', 'IT'],
  ['/app/admin', 'admin_module', 'Administration'],
  ['/app/finance', 'finance', 'Finance & Admin'],
  ['/app/documents', 'documents', 'Documents'],
  ['/app/billing', 'billing', 'Billing'],
  ['/app/contracts', 'billing', 'Variation & Claims'],
  ['/app/ask-erp', 'ask_erp', 'Ask the ERP'],
];

// Exact sidebar entries carry the per-page module key (e.g. /app/crm/enquiries
// is tendering, not crm) — they win over the prefix rules above. The Executive
// Dashboard item (to:'/app') is excluded: it would prefix-match EVERY /app/*
// path and shadow the prefix rules (iter-422 review finding). '/app' itself is
// already handled by PUBLIC_PATHS exact match.
const NAV_RULES = NAV_GROUPS.flatMap((g) =>
  g.items
    .filter((i) => i.to && i.to.startsWith('/app') && i.to !== '/app')
    .map((i) => ({ to: i.to.split('?')[0], key: i.key, label: i.label }))
);

function resolveRule(pathname) {
  if (PUBLIC_PATHS.includes(pathname)) return { public: true };
  for (const r of SUPER_ADMIN_ONLY) {
    if (pathname === r.prefix || pathname.startsWith(r.prefix + '/')) {
      return { super_admin_only: true, label: r.label };
    }
  }
  const navExact = NAV_RULES
    .filter((r) => pathname === r.to || pathname.startsWith(r.to + '/'))
    .sort((a, b) => b.to.length - a.to.length);
  if (navExact.length) {
    const rule = navExact[0];
    if (!rule.key || rule.key === '__public__') return { public: true };
    return { key: rule.key, label: rule.label };
  }
  const pref = PREFIX_RULES
    .filter(([p]) => pathname === p || pathname.startsWith(p + '/'))
    .sort((a, b) => b[0].length - a[0].length);
  if (pref.length) return { key: pref[0][1], label: pref[0][2] };
  return { public: true }; // unmatched paths fall through to NotFound
}

function AccessDenied({ label, permission }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="access-denied-page">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-8 text-center shadow-sm">
        <ShieldAlert size={40} className="mx-auto text-rose-500 mb-4" />
        <h1 className="font-display text-xl font-bold mb-2">Access Denied</h1>
        <p className="text-sm text-slate-600">
          You don&apos;t have permission to view <strong>{label}</strong>.
        </p>
        <p className="text-xs text-slate-500 mt-3">
          Missing permission:{' '}
          <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded" data-testid="access-denied-permission">
            {permission}
          </code>
        </p>
        <Link
          to="/app"
          className="inline-block mt-6 px-4 py-2 bg-[#3b4872] hover:bg-[#2c3658] text-white rounded-sm text-sm font-semibold"
          data-testid="access-denied-home-link"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}

export default function RouteGuard({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Never render the protected page before the permission check can resolve.
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f1a2e] text-white text-sm">
        Loading…
      </div>
    );
  }
  if (!user) return children; // Layout performs the auth redirect

  const rule = resolveRule(location.pathname);
  const role = (user.role || '').toLowerCase();
  const allowed = user.allowed_modules;
  const isAllAccess = Array.isArray(allowed) && allowed.includes('*');
  const isSuper = role === 'super_admin' || isAllAccess;

  if (rule.super_admin_only && !isSuper) {
    return <AccessDenied label={rule.label} permission="super_admin role" />;
  }
  if (rule.key) {
    // Mirrors Sidebar.moduleAllowed, including the legacy `labour` unlock.
    const moduleAllowed =
      isSuper ||
      !Array.isArray(allowed) || // legacy tokens without the claim — sidebar shows all
      allowed.includes(rule.key) ||
      ((rule.key === 'labour_master' || rule.key === 'labour_attendance') && allowed.includes('labour'));
    if (!moduleAllowed) {
      return <AccessDenied label={rule.label} permission={`${rule.key} module access`} />;
    }
  }
  return children;
}
